import ControlFactory from '@/Factories/ControlFactory';
import ComponentManger from '@/Management/ComponentManager';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';
import Utilities from '@/Utility/Utilities';
import TypeConverter from '@/Utility/TypeConverter';
import NumericTextBoxViewModel from '@/ViewModels/Controls/NumericTextBoxViewModel';
import NumericTextBox from '@/Controls/NumericTextBox';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { FilterInfo } from '#/DefaultViewExtensionInterfaces';

export default class NumericTextBoxFactory extends ControlFactory {

    private jQueryControl: JQuery;
    private NumericTextBoxViewModel = ComponentManger.getViewModel<typeof NumericTextBoxViewModel>('NumericTextBoxViewModel');
    private NumericTextBox = ComponentManger.getControl<typeof NumericTextBox>('NumericTextBox');
    protected LabelViewModel = ComponentManger.getViewModel<typeof LabelViewModel>('LabelViewModel');

    /**
     * @override
     */
    public viewModel: NumericTextBoxViewModel;
    
    public min: number;
    public max: number;
    public floatingPrecision: number;

    public constructor(private params: any) {
        super(params);
        this.min = this.paramOrDefault(this.params.min, null);
        this.max = this.paramOrDefault(this.params.max, null);
        this.floatingPrecision = this.paramOrDefault(this.params.floatingPrecision, null)
    }

    /**
     * @override
     */
    control: NumericTextBox;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = this.createViewModel();

        this.control = new this.NumericTextBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.detailViewItem.name]);
        this.registerDefaultValidation([this.detailViewItem.name]);
        this.registerTypeValidation(this.detailViewItem.name, (value: any, dotNetTypeName: any): boolean => {
            if (dotNetTypeName != null) {
                let type = dotNetTypeName;
                if (type != null) {
                    let info = NumericTypeHelper.getDotNetTypeInfo(type);
                    if (info != null) {
                        let min = this.viewModel.min() != null ? Math.max(this.viewModel.min(), info.min) : info.min;
                        let max = this.viewModel.max() != null ? Math.min(this.viewModel.max(), info.max) : info.max;

                        if (value < min || value > max) {
                            return false;
                        }
                    }
                }
            }
            return true;
        })

        return this.jQueryControl;
    }

    public getValueForFilter(): Object {
        var ret = {};

        ret[this.detailViewItem.name] = this.context.genericObject.getValue(this.detailViewItem.name);
        return ret;
    }

    public getFilterInfo(): FilterInfo {
        const currentVal = this.viewModel?.value();
        return {
            field: this.detailViewItem.name,
            value: Utilities.isNullOrUndefined(currentVal)
                ? ''
                : currentVal
        }
    }

    private createViewModel(): NumericTextBoxViewModel {
        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            if (this.detailViewItem.dotNetTypeName == null || this.detailViewItem.dotNetTypeName.indexOf('Double') === -1) {
                length = 255;
            } else {
                length = 326;
            }
        }

        let floatingPrecision = this.floatingPrecision;
        if (Utilities.isNullOrUndefined(floatingPrecision) &&
            (TypeConverter.typeIsNonFloatingNumber(this.detailViewItem.dotNetTypeName) || TypeConverter.typeIsByte(this.detailViewItem.dotNetTypeName)))
            floatingPrecision = 0;


        return new this.NumericTextBoxViewModel({
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            isFocusable: this.isFocusable,
            invalidationText: this.invalidationText,
            floatingPrecision: floatingPrecision,
            maxLength: length,
            min: this.min,
            max: this.max,
            value: this.context.genericObject.getObservable(this.detailViewItem.name),
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            technicalName: this.detailViewItem.name,
            showSpinners: false,
            label: new this.LabelViewModel({
                isVisible: !!this.detailViewItem.unitErgName,
                ergName: this.detailViewItem.unitErgName
            }),
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            onSubmit: this.params.onSubmit,
            dotNetTypeName: this.detailViewItem.dotNetTypeName
        })
    }
}

ComponentManger.setFactory('NumericTextBox', NumericTextBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');